import React, { useState } from 'react';
import { useCalculator } from '../context/CalculatorContext';
import { Download, FileText, CheckCircle, ChevronDown } from 'lucide-react';
import { generateFullReport } from '../utils/reportGenerator';
// @ts-ignore
import html2pdf from 'html2pdf.js';

const DownloadReportButton: React.FC = () => {
  const { investment, results } = useCalculator();
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'downloading' | 'complete'>('idle');
  const [downloadFormat, setDownloadFormat] = useState<'html' | 'pdf'>('html');
  const [showFormatSelector, setShowFormatSelector] = useState(false);

  if (!results) return null;

  const handleDownload = (format: 'html' | 'pdf' = downloadFormat) => {
    setDownloadStatus('downloading');
    
    if (format === 'pdf') {
      // Generate HTML content first
      const htmlContent = generateFullReport(investment, results, true); // true = return content instead of download
      
      // Configure PDF options
      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `Reporte_Completo_Cosecha_Capital_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'in', 
          format: 'letter', 
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      // Create temporary element for PDF generation
      const element = document.createElement('div');
      element.innerHTML = htmlContent;
      
      // Apply PDF-specific styles
      element.style.cssText = `
        width: 7.5in;
        max-width: 7.5in;
        min-height: 10in;
        padding: 0.5in;
        margin: 0;
        background-color: white;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 11px;
        line-height: 1.4;
        color: #1f2937;
        box-sizing: border-box;
        overflow: hidden;
      `;
      
      // Add PDF-specific CSS
      const style = document.createElement('style');
      style.textContent = `
        @page {
          size: letter;
          margin: 0.5in;
        }
        
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        
        body {
          font-size: 11px !important;
          line-height: 1.4 !important;
          max-width: 7.5in !important;
        }
        
        h1 {
          font-size: 18px !important;
          margin-bottom: 8px !important;
          page-break-after: avoid;
        }
        
        h2 {
          font-size: 16px !important;
          margin-bottom: 6px !important;
          page-break-after: avoid;
        }
        
        h3 {
          font-size: 14px !important;
          margin-bottom: 4px !important;
          page-break-after: avoid;
        }
        
        p {
          font-size: 11px !important;
          margin-bottom: 4px !important;
        }
        
        .section {
          margin-bottom: 15px !important;
          padding: 10px !important;
          page-break-inside: avoid;
        }
        
        .grid {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          gap: 8px !important;
          margin-bottom: 8px !important;
        }
        
        .stat-card {
          padding: 8px !important;
          font-size: 10px !important;
        }
        
        table {
          width: 100% !important;
          font-size: 9px !important;
          margin: 8px 0 !important;
        }
        
        th, td {
          padding: 4px 6px !important;
          font-size: 9px !important;
        }
        
        .header {
          text-align: center !important;
          margin-bottom: 20px !important;
          padding: 15px !important;
        }
        
        .footer {
          margin-top: 20px !important;
          padding-top: 10px !important;
          font-size: 10px !important;
        }
        
        .comparison-grid {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          gap: 10px !important;
        }
        
        .comparison-card {
          padding: 10px !important;
          font-size: 10px !important;
        }
        
        .multiplier-highlight {
          padding: 10px !important;
          margin: 10px 0 !important;
          font-size: 11px !important;
        }
        
        @media print {
          body { 
            font-size: 11px !important;
            max-width: 7.5in !important;
          }
          .section {
            page-break-inside: avoid;
          }
        }
      `;
      element.appendChild(style);
      
      // Add to DOM temporarily
      document.body.appendChild(element);
      
      // Generate PDF
      html2pdf().set(opt).from(element).save().then(() => {
        document.body.removeChild(element);
        setDownloadStatus('complete');
        setTimeout(() => setDownloadStatus('idle'), 2000);
      }).catch((error: any) => {
        console.error('Error generating PDF:', error);
        // Fallback to HTML download
        generateFullReport(investment, results);
        document.body.removeChild(element);
        setDownloadStatus('complete');
        setTimeout(() => setDownloadStatus('idle'), 2000);
      });
    } else {
      // Use the report generator utility for HTML
      generateFullReport(investment, results);
      
      setTimeout(() => {
        setDownloadStatus('complete');
        setTimeout(() => setDownloadStatus('idle'), 2000);
      }, 1000);
    }
  };
  // Quick download functions
  const handleQuickDownload = (format: 'html' | 'pdf') => {
    setDownloadFormat(format);
    handleDownload(format);
  };

  return (
    <div className="mt-6 space-y-4">
      {/* Format Selector */}
      <div className="flex justify-center">
        <div className="relative">
          <button
            onClick={() => setShowFormatSelector(!showFormatSelector)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm font-medium text-gray-700">
              Formato: {downloadFormat.toUpperCase()}
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showFormatSelector ? 'rotate-180' : ''}`} />
          </button>
          
          {showFormatSelector && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowFormatSelector(false)}
              />
              <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                <button
                  onClick={() => {
                    setDownloadFormat('html');
                    setShowFormatSelector(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors first:rounded-t-lg ${
                    downloadFormat === 'html' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <span className="text-lg">📄</span>
                  <div>
                    <div className="text-sm font-medium">HTML</div>
                    <div className="text-xs text-gray-500">Visualización web</div>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setDownloadFormat('pdf');
                    setShowFormatSelector(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors last:rounded-b-lg ${
                    downloadFormat === 'pdf' ? 'bg-red-50 text-red-700' : 'text-gray-700'
                  }`}
                >
                  <span className="text-lg">📋</span>
                  <div>
                    <div className="text-sm font-medium">PDF</div>
                    <div className="text-xs text-gray-500">Documento profesional</div>
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Main Download Button */}
      <div className="flex justify-center">
        <button
          onClick={() => handleDownload(downloadFormat)}
          disabled={downloadStatus === 'downloading'}
          className={`
            flex items-center justify-center gap-2 px-8 py-4 rounded-lg shadow-lg
            transition-all duration-300 transform hover:scale-105
            ${downloadStatus === 'complete' 
              ? 'bg-green-600 text-white' 
              : downloadFormat === 'pdf'
                ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700'
                : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
            }
          `}
        >
          {downloadStatus === 'idle' && (
            <>
              <Download className="w-5 h-5" />
              <span className="font-medium">
                Certificado contra Otras Inversiones ({downloadFormat.toUpperCase()})
              </span>
            </>
          )}
          {downloadStatus === 'downloading' && (
            <>
              <FileText className="w-5 h-5 animate-pulse" />
              <span className="font-medium">Generando {downloadFormat.toUpperCase()}...</span>
            </>
          )}
          {downloadStatus === 'complete' && (
            <>
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">¡Reporte {downloadFormat.toUpperCase()} Descargado!</span>
            </>
          )}
        </button>
      </div>
      
      {/* Quick Download Buttons */}
      <div className="flex justify-center gap-3">
        <button
          onClick={() => handleQuickDownload('html')}
          disabled={downloadStatus === 'downloading'}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm flex items-center gap-2"
        >
          <span>📄</span>
          <span>HTML</span>
        </button>
        <button
          onClick={() => handleQuickDownload('pdf')}
          disabled={downloadStatus === 'downloading'}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm flex items-center gap-2"
        >
          <span>📋</span>
          <span>PDF</span>
        </button>
      </div>
    </div>
  );
};

export default DownloadReportButton;